/**
 * Last Auth Provider — Remembers which OAuth provider the user last signed in with.
 *
 * Stores the provider name in localStorage so returning visitors see a
 * "Last signed in with [Provider]" hint above the relevant OAuth button.
 *
 * Graceful degradation: if localStorage is unavailable (private browsing,
 * storage quota, etc.), getLastAuthProvider returns null and
 * setLastAuthProvider is a no-op.
 */

import { createLogger } from "../utils/logger";

const authProviderLogger = createLogger({ prefix: "LastAuthProvider" });

export const LAST_AUTH_PROVIDER_KEY = "retireez_last_auth_provider";

const VALID_PROVIDERS = new Set(["google", "apple", "microsoft"]);

export type AuthProviderName = "google" | "apple" | "microsoft";

/**
 * Store the most recently used auth provider in localStorage.
 */
export function setLastAuthProvider(provider: AuthProviderName): void {
  try {
    localStorage.setItem(LAST_AUTH_PROVIDER_KEY, provider);
  } catch (error) {
    authProviderLogger.debug(
      "localStorage unavailable for auth provider write",
      { error }
    );
  }
}

/**
 * Retrieve the most recently used auth provider from localStorage.
 * Returns null if no valid provider is stored or localStorage is unavailable.
 */
export function getLastAuthProvider(): AuthProviderName | null {
  try {
    const stored = localStorage.getItem(LAST_AUTH_PROVIDER_KEY);
    if (stored && VALID_PROVIDERS.has(stored)) {
      return stored as AuthProviderName;
    }
    return null;
  } catch (error) {
    authProviderLogger.debug(
      "localStorage unavailable for auth provider read",
      { error }
    );
    return null;
  }
}
